// UserDetailsPage.jsx
import React, { useState, useEffect } from "react";
import "./admin-user-detail.css";
import { useNavigate, useParams } from "react-router-dom";
import ToastNotification, {
  showToast,
} from "../../components/toasts/ToastNotification";
import { getUser } from "../../api/user.api";
import { HashLoader } from "react-spinners";
import { getUserRankImageURL, paymentMethodText, productTypeText, statusText, userRankText, userStatusText } from "../../utils";
import { hasPermission } from "../../utils/auth";
import { useUser } from "../../context/UserContext";
import { getUserLogin } from "../../api/interaction.api";
import { getUserRecentOrders } from "../../api/order.api";
import { getUserActionHistory, getUserDepositHistory } from "../../api/transaction.api";
import { FaEye } from "react-icons/fa6";

const ITEM_PER_PAGE = 10;
export const AdminUserDetail = () => {
  const { userId } = useParams();
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [userLogin, setUserLogin] = useState([]);
  const [userOrders, setUserOrders] = useState([]);
  const [userDeposit, setUserDeposit] = useState([]);
  const [actions, setActions] = useState([]);
  const navigate = useNavigate();
  const { user } = useUser();

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    setLoading(true);

    try {
      const res = await getUser(userId);
      const userLoginRes = await getUserLogin(userId);
      const userRecentOrdersRes = await getUserRecentOrders(userId);
      const userDepositHistoryRes = await getUserDepositHistory(userId);
      const actionsReponse = await getUserActionHistory(userId);

      if (res?.success && userLoginRes?.success && actionsReponse?.success) {
        setUserData(res.data.user || null);
        setUserLogin(userLoginRes.data.interactions || []);
        setActions(actionsReponse.data.actions || []);
        setUserOrders(userRecentOrdersRes.data.orders || []);
        setUserDeposit(userDepositHistoryRes.data.deposit_history || []);
        document.title = `Người dùng: ${res.data.user?.username}`;
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Function to handle editing the profile (placeholder)
  const handleEditProfile = () => {
    navigate(`/super-admin/users/edit/${userId}`);
  };

  // Function to navigate back to dashboard (placeholder)
  const handleBackToDashboard = () => {
    navigate("/super-admin/users");
  };

  if (loading) {
    return (
      <div className="loader-container">
        <HashLoader color="#092339" />
      </div>
    );
  }

  return (
    <div className="user-detail-container">
      <ToastNotification />
      <header className="user-detail-header">
        <h1 className="user-header-id">#{userId}</h1>
        <div>
          <button
            className="user-detail-action-button"
            onClick={handleBackToDashboard}
          >
            Quay lại
          </button>
        </div>
      </header>

      <div className="user-detail-profile-section">
        <div className="user-detail-profile-card">
          <img
            src={userData?.profileImg}
            alt="Profile"
            className="user-detail-profile-image"
          />
          <h2 className="user-detail-user-name">{userData?.username}</h2>
          <p className="user-detail-user-username">{userData?.email}</p>

          <div
            className={`user-detail-status-badge user-detail-status-${userData?.status}`}
          >
            {userStatusText(userData?.status)}
          </div>

          <div
            className={`user-detail-user-rank user-detail-rank-${userData?.rank}`}
          >
            <div className="user-detail-rank-container">
              <img
                className="user-rank-icon"
                src={getUserRankImageURL(userData?.rank) || null}
                alt="User Rank"
              />
              <p>{userRankText(userData?.rank)}</p>
            </div>
          </div>

          <div className="user-detail-edit-button">
            {hasPermission(user?.role, "edit:user") &&
              <button
                className="user-detail-action-button"
                onClick={handleEditProfile}
              >
                Chỉnh sửa hồ sơ
              </button>}
          </div>
        </div>

        <div className="user-detail-profile-info">
          <div className="user-detail-info-section">
            <h2>Thông tin cơ bản</h2>
            <div className="user-detail-info-grid">
              <div className="user-detail-info-item">
                <div className="user-detail-info-label">Họ và tên</div>
                <div className="user-detail-info-value">
                  {userData?.fullName || "Chưa cung cấp"}
                </div>
              </div>
              <div className="user-detail-info-item">
                <div className="user-detail-info-label">Email</div>
                <div className="user-detail-info-value">{userData?.email}</div>
              </div>
              <div className="user-detail-info-item">
                <div className="user-detail-info-label">Số điện thoại</div>
                <div className="user-detail-info-value">
                  {userData?.phone || "Chưa cung cấp"}
                </div>
              </div>
              <div className="user-detail-info-item">
                <div className="user-detail-info-label">Vai trò</div>
                <div className="user-detail-info-value">
                  {userData?.role.charAt(0).toUpperCase() +
                    userData?.role.slice(1)}
                </div>
              </div>
              <div className="user-detail-info-item">
                <div className="user-detail-info-label">Thành viên từ</div>
                <div className="user-detail-info-value">
                  {new Date(userData?.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>

          <div className="user-detail-info-section user-detail-financial-section">
            <h2>Thông tin tài chính</h2>
            <div className="user-detail-financial-grid">
              <div className="user-detail-financial-card">
                <div className="user-detail-financial-label">
                  Số dư hiện tại
                </div>
                <div className="user-detail-financial-value">
                  {userData?.balance.toLocaleString()}đ
                </div>
              </div>
              <div className="user-detail-financial-card">
                <div className="user-detail-financial-label">
                  Tổng tiền đã nạp
                </div>
                <div className="user-detail-financial-value">
                  {userData?.total_amount_deposited.toLocaleString()}đ
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="user-detail-login-section">
        <h3>Lịch sử đăng nhập</h3>
        <div className="table-responsive">
          <table className="user-detail-table">
            <thead>
              <tr>
                <th>STT</th>
                <th>Hành động</th>
                <th>Thời gian</th>
              </tr>
            </thead>
            <tbody>
              {userLogin.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center">Không có dữ liệu đăng nhập</td>
                </tr>
              ) : (
                userLogin.map((row, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>Đăng nhập</td>
                    <td>{new Date(row?.createdAt).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="user-detail-login-section">
        <h3>Lịch sử chỉnh sửa số dư</h3>
        <div className="table-responsive">
          <table className="user-detail-table">
            <thead>
              <tr>
                <th>STT</th>
                <th>Số tiền</th>
                <th>Người</th>
                <th>Lý do</th> 
                <th>Thời gian</th>
              </tr>
            </thead>
            <tbody>
              {actions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center">Không có dữ liệu chỉnh sửa</td>
                </tr>
              ) : (
                actions.map((row, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{row.transactionType === 'add' ? '+':'-'} {row.amount}</td>
                    <td>{row.author}</td>
                    <td>{row.reason}</td>
                    <td>{new Date(row?.createdAt).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="user-detail-login-section">
        <h3>Lịch sử nạp tiền</h3>
        <div className="table-responsive">
          <table className="user-detail-table">
            <thead>
              <tr>
                <th>STT</th>
                <th>Mã giao dịch</th>
                <th>Số tiền</th>
                <th>Phương thức</th>
                <th>Trạng thái</th>
                <th>Thông tin</th>
                <th>Thời gian</th>
              </tr>
            </thead>
            <tbody>
              {userDeposit.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center">Không có dữ liệu nạp tiền</td>
                </tr>
              ) : (
                userDeposit.map((row, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{row.transactionId}</td>
                    <td>{row.amount.toLocaleString() || 0}</td>
                    <td>{paymentMethodText(row.paymentMethod) || "NA"}</td>
                    <td>{statusText(row.transactionStatus) || "Empty status"}</td>
                    <td>
                      {row?.transaction_attributes ? (
                        <>
                          <p>Serial: {row?.transaction_attributes.serial}</p>
                          <p>Code: {row?.transaction_attributes.code}</p>
                        </>
                      ) : ""}
                    </td>
                    <td>{new Date(row?.createdAt).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="user-detail-login-section">
        <h3>Lịch sử mua hàng</h3>
        <div className="table-responsive">
          <table className="user-detail-table">
            <thead>
              <tr>
                <th>STT</th>
                <th>Mã đơn hàng</th>
                <th>Giá</th>
                <th>Loại</th>
                <th>Trạng thái</th>
                <th>Thời gian</th>
                <th>Hành động</th>
              </tr>
            </thead>
            <tbody>
              {userOrders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center">Không có dữ liệu đơn hàng</td>
                </tr>
              ) : (
                userOrders.map((row, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{row.orderId || ""}</td>
                    <td>{row.order_final_amount.toLocaleString() || 0}</td>
                    <td>{productTypeText(row.order_type)}</td>
                    <td>{statusText(row.order_status) || "Empty status"}</td>
                    <td>{new Date(row?.createdAt).toLocaleString()}</td>
                    <td className="action-cell">
                      <div className="action-buttons-container">
                        <button
                          className="action-button"
                          onClick={() => navigate(`/super-admin/orders/view/${row.orderId}`)}
                        >
                          <FaEye className="action-icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
